import { useState, useMemo, useRef, memo } from 'react';
import dayjs from 'dayjs';
import { FaClock, FaCheckCircle, FaChevronDown, FaChevronUp, FaEdit, FaCheck, FaRedo, FaPlus, FaTrash } from 'react-icons/fa';
import { Button } from 'react-bootstrap';
import { useReportedImpacts } from '../Incidents/hooks/useReportedImpacts';
import { impactService } from '../../../services/impactService';

import ImpactFormModal from '../../Impact/ImpactFormModal';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';
import RefreshIndicator from '../Incidents/components/RefreshIndicator';
import { isResolvedImpact } from '../../../utils/impactStatus';
import { canDeleteImpact } from '../../../utils/privilegeHelpers';
import styles from './IncidentReportedImpacts.module.css';

const INITIAL_COUNT = 5;

/**
 * Checks whether the given impact was created by the specified user email.
 *
 * @param {Object} impact - Impact record
 * @param {string} email  - Current user email
 * @returns {boolean} True if the impact was created by this user
 */
const isOwnImpact = (impact, email) =>
  !!(email && impact.createdBy?.toLowerCase() === email.toLowerCase());

/**
 * Formats a timestamp for display in card footer.
 *
 * @param {string} timestamp - ISO 8601 timestamp
 * @returns {string} Formatted date string
 */
function formatTimestamp(timestamp) {
  if (!timestamp) return '';
  return dayjs(timestamp).format('MMM D, YYYY h:mm A');
}

/**
 * Extracts display name from email by taking the part before '@'.
 *
 * @param {string} email - Email address
 * @returns {string} Display name portion
 */
function displayName(email) {
  if (!email) return 'Unknown';
  const atIdx = email.indexOf('@');
  return atIdx > 0 ? email.substring(0, atIdx) : email;
}

/**
 * Determines whether an impact record has been edited after creation.
 *
 * @param {Object} impact - Impact record
 * @returns {boolean} True if modified after creation
 */
function isEdited(impact) {
  if (!impact.lastModifiedAt || !impact.createdAt) return false;
  return new Date(impact.lastModifiedAt) > new Date(impact.createdAt);
}

/**
 * IncidentReportedImpacts
 *
 * Displays reported impacts for an incident with edit/resolve actions.
 * Data fetching is handled by the useReportedImpacts hook.
 *
 * @param {Object}  props
 * @param {string}  props.problemId   - Problem number
 * @param {Object}  props.user        - Authenticated user object
 * @param {Function} [props.onImpactCreated] - Callback fired after a successful create
 * @param {string}  [props.snowHost]  - ServiceNow base URL for INC deep-links
 * @param {boolean} [props.isResolved] - When true, the "Report Impact" button is hidden
 *                                       (incident is in a Resolve state — read-only view)
 * @param {boolean} [props.isTemporary] - When true, impacts are not fetched: the
 *                                        backend refuses impact reads on a temporary
 *                                        record. Affects FETCHING only — the
 *                                        "Report Impact" button is gated by
 *                                        `isResolved` alone. Pass the record's
 *                                        `temporary` flag, not a number prefix.
 * @param {boolean} [props.detailLoading] - When true, the caller has not yet resolved
 *                                        the record that decides `isTemporary`, so this
 *                                        component renders its loading skeleton instead
 *                                        of an empty state. Required whenever a caller
 *                                        passes `isTemporary` conservatively while its
 *                                        own fetch is in flight: with fetching disabled
 *                                        the hook reports `loading === false` and
 *                                        `impacts === []`, which would otherwise render
 *                                        "No impacts reported yet." for the whole of the
 *                                        caller's fetch window. Pass a value coincident
 *                                        with that window (see
 *                                        ModernIncidentDetailPage's `detailLoading={!data}`).
 */
function IncidentReportedImpacts({ problemId, user, onImpactCreated, snowHost = 'https://cox.service-now.com', isResolved = false, isTemporary = false, detailLoading = false }) {
  // US1950081: PREVENT THE REQUEST, DO NOT REACT TO ITS FAILURE. Temporary problems
  // are ineligible for impact reporting and the backend answers 403 by design
  // (handler_impact.go:372, `if problem.Temporary`), so asking is always futile.
  //
  // The gate reads the `temporary` FLAG because that is the exact field the backend
  // refuses on. A `PRBT` number-prefix test would be a second, independent definition
  // of "temporary" that nothing keeps in sync with the backend. Same flag is already
  // read at ProblemForm.js:3297 and ImpactViewPage.js:88.
  //
  // THIS IS THE ONLY FETCH GATE, and both callers feed it: the
  // <IncidentReportedImpacts> element at ExpandedContent.js:171-177 passes
  // `isTemporary={!!data.temporary}` (the prop is the line at :176) off an
  // already-non-null record, and
  // ModernIncidentDetailPage.js:225 passes `!data || !!data.temporary` so the
  // null-`data` first render of a PRBT link does not ask either. A superseded revision
  // of this comment said ExpandedContent "declines to render this component at all for
  // a temporary problem" — it does not. Gating that section on `!data.temporary` hid
  // the heading and broke e2e MUD-02, so it passes the prop instead
  // (ExpandedContent.js:164-177). There is no second layer behind this one.
  const {
    impacts,
    loading,
    refreshing,
    lastUpdated,
    error,
    refresh,
    markResolved,
    markActive
  } = useReportedImpacts(problemId, !isTemporary, user?.email);

  const [showAll, setShowAll] = useState(false);
  const [editingImpact, setEditingImpact] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [actioningId, setActioningId] = useState(null);
  const [editError, setEditError] = useState(null);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [deletingImpact, setDeletingImpact] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  // US1971073: dedicated in-flight flag for delete so a delete-in-progress drives
  // the Delete button's own "Deleting…" label rather than reusing actioningId
  // (which would make the still-visible Resolve button read "Resolving…").
  const [deletingId, setDeletingId] = useState(null);
  const editTriggerRef = useRef(null);
  const cardRefs = useRef({});

  // US1971073: hard-delete of erroneous impact entries is restricted to the
  // MIM/Eternium personas (impact_delete) or deity. The server independently
  // re-enforces this; hiding the button is defense-in-depth.
  const canDelete = canDeleteImpact(user);

  // Compute summary counts locally
  const { openCount, resolvedCount } = useMemo(() => {
    let open = 0;
    let resolved = 0;
    for (const impact of impacts) {
      if (isResolvedImpact(impact)) {
        resolved++;
      } else {
        open++;
      }
    }
    return { openCount: open, resolvedCount: resolved };
  }, [impacts]);

  const userEmail = user?.email || '';

  // Show More / Show Less
  const visibleImpacts = showAll ? impacts : impacts.slice(0, INITIAL_COUNT);
  const hiddenCount = impacts.length - INITIAL_COUNT;
  const showExpandButton = impacts.length > INITIAL_COUNT;

  const handleEditClick = (impact, triggerEl) => {
    setEditError(null);
    editTriggerRef.current = triggerEl;
    setEditingImpact(impact);
    setShowEditModal(true);
  };

  const handleSubmitEdit = async (formData) => {
    setSubmitting(true);
    setEditError(null);
    try {
      const statusDisplayMap = {
        active: 'Active',
        resolved: 'Resolved'
      };

      const impactData = {
        ...formData,
        statusDisplay: statusDisplayMap[formData.status] || formData.statusDisplay
      };

      if (impactData.ci) {
        impactData.ci = impactData.ci.trim().toUpperCase();
      }

      // Spreads the full original impact to satisfy PUT validation, then overlays form data.
      // Concurrent edits are last-write-wins — no optimistic lock.
      await impactService.updateImpact(problemId, editingImpact.id, {
        ...editingImpact,
        ...impactData
      });
      setEditError(null);
      setShowEditModal(false);
      setEditingImpact(null);
      await refresh();
    } catch (err) {
      console.error('Failed to update impact:', err);
      setEditError(err.message || 'Failed to update impact. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkResolved = async (impact) => {
    const impactId = impact.id;
    setEditError(null);
    setActioningId(impactId);
    try {
      await markResolved(impact);
    } catch (err) {
      console.error('Failed to mark impact as resolved:', err);
      setEditError(err.message || 'Failed to mark impact as resolved. Please try again.');
    } finally {
      setActioningId(null);
      setTimeout(() => { cardRefs.current[impactId]?.focus(); }, 0);
    }
  };

  const handleMarkActive = async (impact) => {
    const impactId = impact.id;
    setEditError(null);
    setActioningId(impactId);
    try {
      await markActive(impact);
    } catch (err) {
      console.error('Failed to re-open impact:', err);
      setEditError(err.message || 'Failed to re-open impact. Please try again.');
    } finally {
      setActioningId(null);
      setTimeout(() => { cardRefs.current[impactId]?.focus(); }, 0);
    }
  };

  const handleDeleteClick = (impact) => {
    setDeleteError(null);
    setDeletingImpact(impact);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingImpact) return;
    const impactId = deletingImpact.id;
    setEditError(null);
    setDeleteError(null);
    setDeletingId(impactId);
    try {
      await impactService.deleteImpact(problemId, impactId);
      await refresh();
    } catch (err) {
      console.error('Failed to delete impact:', err);
      setDeleteError(err.message || 'Failed to delete impact. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleSubmitCreate = async (formData) => {
    setCreateSubmitting(true);
    setCreateError(null);
    try {
      await impactService.createImpact(problemId, formData);
      // Close modal immediately — do not block on refresh
      setReportModalOpen(false);
      refresh(); // fire-and-forget; fetchImpacts never rejects
    } catch (err) {
      console.error('Failed to create impact:', err);
      setCreateError(err.message || 'Failed to create impact. Please try again.');
      return;
    } finally {
      setCreateSubmitting(false);
    }
    // Isolated try/catch: onImpactCreated is intentionally outside the create
    // try/catch/finally above. The catch block above returns early on failure,
    // so this line only executes after a successful create.
    try {
      await onImpactCreated?.();
    } catch (cbErr) {
      console.error('onImpactCreated callback failed (non-fatal):', cbErr);
    }
  };

  const sectionHeader = (
    <div className={styles.sectionHeader}>
      <h2 className={styles.sectionTitle} data-testid="reported-impacts-heading">Reported Impacts</h2>
      {!isResolved && (
        <Button
          variant="danger"
          size="sm"
          className="d-inline-flex align-items-center gap-1"
          disabled={loading}
          onClick={() => { setReportModalOpen(true); setCreateError(null); }}
          data-testid="report-impact-button"
        >
          <FaPlus /> Report Impact
        </Button>
      )}
    </div>
  );

  // `detailLoading` is ORed in, not folded into the hook: with fetching disabled the hook
  // legitimately reports loading=false / impacts=[], and that pair is indistinguishable from
  // "fetched, none found". Only the caller knows its own record is still in flight.
  if (loading || detailLoading) {
    return (
      <>
        {sectionHeader}
        <div
          className={styles.container}
          role="status"
          aria-busy="true"
          aria-label="Loading reported impacts"
        >
          <div className={styles.skeletonCard} />
          <div className={styles.skeletonCard} />
          <div className={styles.skeletonCard} />
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        {sectionHeader}
        <div className={styles.container}>
          <p className={styles.errorState}>{error}</p>
        </div>
      </>
    );
  }

  if (impacts.length === 0) {
    return (
      <>
        {sectionHeader}
        <div className={styles.container}>
          <p className={styles.emptyState} data-testid="reported-impacts-empty-state">No impacts reported yet.</p>
          {createError && (
            <div className={styles.errorState} role="alert">
              {createError}
            </div>
          )}
        </div>
        <ImpactFormModal
          show={reportModalOpen}
          onHide={() => { setReportModalOpen(false); setCreateError(null); }}
          onSubmit={handleSubmitCreate}
          initialValues={null}
          problemNumber={problemId}
          loading={createSubmitting}
          error={createError}
        />
      </>
    );
  }

  return (
    <>
    {sectionHeader}
    <div className={styles.container}>
      {/* Summary bar */}
      <div className={styles.summaryBar}>
        <span className={styles.summaryItem} data-testid="reported-impacts-open-count">
          <FaClock className={styles.summaryIconOpen} aria-hidden="true" />
          Open: {openCount}
        </span>
        <span className={styles.summaryDot} aria-hidden="true">&bull;</span>
        <span className={styles.summaryItem} data-testid="reported-impacts-resolved-count">
          <FaCheckCircle className={styles.summaryIconResolved} aria-hidden="true" />
          Resolved: {resolvedCount}
        </span>
        <RefreshIndicator
          isRefreshing={refreshing}
          lastUpdated={lastUpdated}
          showTimestamp={!refreshing && !!lastUpdated}
        />
      </div>

      {/* Inline error for mutation failures */}
      {editError && (
        <div className={styles.errorState} role="alert">
          {editError}
        </div>
      )}
      {createError && (
        <div className={styles.errorState} role="alert">
          {createError}
        </div>
      )}
      {deleteError && (
        <div className={styles.errorState} role="alert">
          {deleteError}
        </div>
      )}

      {/* Impact cards */}
      {visibleImpacts.map((impact) => {
        const isActive = impact.status === 'active';
        const edited = isEdited(impact);
        const isActioning = actioningId === impact.id;
        const isDeleting = deletingId === impact.id;
        const isBusy = isActioning || isDeleting;
        const isOwn = isOwnImpact(impact, userEmail);

        return (
          <div
            key={impact.id}
            ref={el => { cardRefs.current[impact.id] = el; }}
            tabIndex="-1"
            className={`${styles.impactCard} ${isActive ? styles.impactCardActive : styles.impactCardResolved} ${isOwn ? styles.impactCardOwn : ''}`}
          >
            <div className={styles.cardHeader}>
              <div className={styles.cardHeaderLeft}>
                <div>
                  <span className={styles.appName}>{impact.applicationName}</span>
                  {edited && <span className={styles.editedTag}>(edited)</span>}
                </div>
                <span className={`${styles.statusText} ${isActive ? styles.statusActive : styles.statusResolved}`}>
                  {impact.statusDisplay || impact.status}
                </span>
              </div>
              <div className={styles.actions}>
                <button
                  type="button"
                  className={`${styles.actionBtn} ${styles.actionBtnEdit}`}
                  onClick={(e) => handleEditClick(impact, e.currentTarget)}
                  title="Edit impact"
                  disabled={isBusy}
                >
                  <FaEdit aria-hidden="true" /> Edit
                </button>
                {isActive ? (
                  <button
                    type="button"
                    className={`${styles.actionBtn} ${styles.actionBtnResolve}`}
                    onClick={() => handleMarkResolved(impact)}
                    title="Mark as resolved"
                    disabled={isBusy}
                  >
                    <FaCheck aria-hidden="true" /> {isActioning ? 'Resolving…' : 'Mark Resolved'}
                  </button>
                ) : (
                  <button
                    type="button"
                    className={`${styles.actionBtn} ${styles.actionBtnReopen}`}
                    onClick={() => handleMarkActive(impact)}
                    title="Re-open impact"
                    disabled={isBusy}
                  >
                    <FaRedo aria-hidden="true" /> {isActioning ? 'Reopening…' : 'Re-open'}
                  </button>
                )}
                {canDelete && (
                  <button
                    type="button"
                    className={`${styles.actionBtn} ${styles.actionBtnDelete}`}
                    onClick={() => handleDeleteClick(impact)}
                    title="Delete impact"
                    disabled={isBusy}
                    data-testid="delete-impact-button"
                  >
                    <FaTrash aria-hidden="true" /> {isDeleting ? 'Deleting…' : 'Delete'}
                  </button>
                )}
              </div>
            </div>

            <div className={styles.description}>
              {impact.impactDescription || <span className={styles.emptyDescription}>No description provided.</span>}
            </div>

            <div className={styles.cardFooter}>
              <span className={styles.footerItem}><strong>Reported by:</strong> {displayName(impact.createdBy)}</span>
              {impact.brand && (
                <>
                  <span className={styles.footerSeparator}>&bull;</span>
                  <span className={styles.footerItem}><strong>Brand:</strong> {impact.brand}</span>
                </>
              )}
              <span className={styles.footerSeparator}>&bull;</span>
              <span className={styles.footerItem}><strong>Reported:</strong> {formatTimestamp(impact.createdAt)}</span>
              {impact.ci && (
                <>
                  <span className={styles.footerSeparator}>&bull;</span>
                  <span className={styles.footerItem}>
                    <strong>CI:</strong>{' '}
                    <a
                      href={`https://app.getcortexapp.com/admin/entities?tag=${encodeURIComponent(impact.ci)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.ciLink}
                    >
                      {impact.ci}
                    </a>
                  </span>
                </>
              )}
              {impact.childIncidentNumber && (
                <>
                  <span className={styles.footerSeparator}>&bull;</span>
                  <span className={styles.footerItem}>
                    <strong>INC:</strong>{' '}
                    <a
                      href={`${snowHost}/nav_to.do?uri=${encodeURIComponent(`/incident.do?sysparm_query=number=${impact.childIncidentNumber}`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.ciLink}
                    >
                      {impact.childIncidentNumber}
                    </a>
                  </span>
                </>
              )}
              {!isActive && impact.lastModifiedAt && (
                <>
                  <span className={styles.footerSeparator}>&bull;</span>
                  <span className={styles.footerItem}><strong>Resolved:</strong> {formatTimestamp(impact.lastModifiedAt)} by {displayName(impact.lastModifiedBy)}</span>
                </>
              )}
            </div>
          </div>
        );
      })}

      {/* Show More / Show Less */}
      {showExpandButton && (
        <button
          type="button"
          className={styles.expandButton}
          onClick={() => setShowAll((prev) => !prev)}
          aria-expanded={showAll}
          aria-label={
            showAll
              ? 'Show fewer reported impacts'
              : `Show ${hiddenCount} more ${hiddenCount === 1 ? 'impact' : 'impacts'}`
          }
        >
          {showAll ? (
            <>
              Show less
              <FaChevronUp className={styles.expandIcon} aria-hidden="true" />
            </>
          ) : (
            <>
              Show {hiddenCount} more {hiddenCount === 1 ? 'impact' : 'impacts'}
              <FaChevronDown className={styles.expandIcon} aria-hidden="true" />
            </>
          )}
        </button>
      )}

      {/* Edit Impact Modal */}
      <ImpactFormModal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setEditingImpact(null);
          setEditError(null);
          editTriggerRef.current?.focus();
        }}
        onSubmit={handleSubmitEdit}
        initialValues={editingImpact}
        loading={submitting}
        problemNumber={problemId}
        title="Edit Impact"
        error={editError}
      />
      <ImpactFormModal
        show={reportModalOpen}
        onHide={() => { setReportModalOpen(false); setCreateError(null); }}
        onSubmit={handleSubmitCreate}
        initialValues={null}
        problemNumber={problemId}
        loading={createSubmitting}
        error={createError}
      />

      {/* US1971073: Delete confirmation (MIM/Eternium only) — hard delete, no undo */}
      <ConfirmationModal
        isOpen={!!deletingImpact}
        onClose={() => setDeletingImpact(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Impact"
        message={
          deletingImpact
            ? `Delete the reported impact for "${deletingImpact.applicationName}"? This permanently removes the entry and cannot be undone.`
            : ''
        }
        confirmText="Delete"
        variant="danger"
      />
    </div>
    </>
  );
}

export default memo(IncidentReportedImpacts);
